import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Phone, Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    plotSize: '',
    requirements: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    toast({
      title: 'Quote Request Submitted!',
      description: 'Our team will contact you within 24 hours.',
    });
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      location: '',
      plotSize: '',
      requirements: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-4">
            Ready to Build Your <span className="text-orange-600">Dream Home?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Fill out the form below and our construction experts will get in touch with you
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-3 rounded-xl">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Phone Number</p>
                    <p className="text-lg font-semibold text-gray-900">+91 97384 77839</p>
                    {/*<p className="text-lg font-semibold text-gray-900">+91 98765 </p>*/}
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-3 rounded-xl">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email Address</p>
                    <p className="text-lg font-semibold text-gray-900">salmanazeezkhan@gmail.com</p>
                    {/*<p className="text-lg font-semibold text-gray-900">support@s4homes.com</p>*/}
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-3 rounded-xl">
                    <MapPin className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Locations</p>
                    <p className="text-lg font-semibold text-gray-900">Bangalore, Kolar</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-orange-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>10 Years Waterproofing Warranty</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>98% On-Time Delivery Rate</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>No Hidden Charges</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Fixed Cost Pricing</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                  Location *
                </label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({ ...formData, location: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bangalore">Bangalore</SelectItem>
                    <SelectItem value="kolar">Kolar</SelectItem>
                    <SelectItem value="mysuru">Mysuru</SelectItem>
                    <SelectItem value="other">Other Cities</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="plotSize" className="block text-sm font-semibold text-gray-700 mb-2">
                  Plot Size *
                </label>
                <Select
                  value={formData.plotSize}
                  onValueChange={(value) => setFormData({ ...formData, plotSize: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select plot size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="810-1200">810 - 1200 Sq. ft.</SelectItem>
                    <SelectItem value="1200-1500">1200 - 1500 Sq. ft.</SelectItem>
                    <SelectItem value="1500-2400">1500 - 2400 Sq. ft.</SelectItem>
                    <SelectItem value="2400+">Above 2400 Sq. ft.</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="requirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  Construction Requirements *
                </label>
                <Textarea
                  id="requirements"
                  name="requirements"
                  required
                  value={formData.requirements}
                  onChange={handleChange}
                  placeholder="Tell us about your construction requirements..."
                  className="w-full min-h-32"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold"
              >
                Submit Request
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to receive updates via Email/SMS/WhatsApp/Call
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;